/**
 * Goals Performance API Service
 * All API calls for the Goals Performance system
 */

import { apiInstance } from '../hooks/useApi';

const BASE_URL = '/api/v1/goals-performance';

// ============================================================================
// SETTINGS APIs
// ============================================================================

export const getGoalsSettings = async () => {
  const response = await apiInstance.get(`${BASE_URL}/settings`);
  return response.data;
};

export const updateGoalsSettings = async (settings) => {
  const response = await apiInstance.put(`${BASE_URL}/settings`, settings);
  return response.data;
};

export const uploadGoalsSettingsIcon = async (periodType, iconFile) => {
  const formData = new FormData();
  formData.append('icon', iconFile);
  formData.append('periodType', periodType);

  const response = await apiInstance.post(`${BASE_URL}/settings/upload-icon`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const uploadBadgeIcon = async (badgeIndex, iconFile) => {
  const formData = new FormData();
  formData.append('icon', iconFile);
  formData.append('badgeIndex', badgeIndex);

  const response = await apiInstance.post(`${BASE_URL}/settings/upload-badge-icon`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const uploadStreakIcon = async (iconFile) => {
  const formData = new FormData();
  formData.append('icon', iconFile);

  const response = await apiInstance.post(`${BASE_URL}/settings/upload-streak-icon`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// ============================================================================
// CYCLE MANAGEMENT APIs
// ============================================================================

export const getCurrentCycle = async () => {
  const response = await apiInstance.get(`${BASE_URL}/cycles/current`);
  return response.data;
};

export const getCycles = async (filters = {}) => {
  const response = await apiInstance.get(`${BASE_URL}/cycles`, { params: filters });
  return response.data;
};

export const getCycleById = async (cycleId) => {
  const response = await apiInstance.get(`${BASE_URL}/cycles/${cycleId}`);
  return response.data;
};

export const finalizeCycle = async (cycleId) => {
  const response = await apiInstance.put(`${BASE_URL}/cycles/${cycleId}/finalize`);
  return response.data;
};

/** Org-admin: reopen submission for one member until reopenedUntil (ISO datetime). */
export const reopenSubmissionForMember = async (cycleId, { userId, reopenedUntil, reason } = {}) => {
  const response = await apiInstance.post(`${BASE_URL}/cycles/${cycleId}/submission-exceptions`, {
    userId,
    reopenedUntil,
    reason,
  });
  return response.data;
};

export const getSubmissionException = async (cycleId, userId) => {
  const response = await apiInstance.get(`${BASE_URL}/cycles/${cycleId}/submission-exceptions/${userId}`);
  return response.data;
};

export const revokeSubmissionException = async (cycleId, userId) => {
  const response = await apiInstance.delete(`${BASE_URL}/cycles/${cycleId}/submission-exceptions/${userId}`);
  return response.data;
};

// ============================================================================
// REPORTEES CHECK API
// ============================================================================

export const checkReportees = async () => {
  const response = await apiInstance.get(`${BASE_URL}/my-reportees`);
  return response.data;
};

// ============================================================================
// GOALS CRUD APIs
// ============================================================================

export const getGoals = async (filters = {}) => {
  const response = await apiInstance.get(`${BASE_URL}/goals`, { params: filters });
  return response.data;
};

/** Admin-only: all goals marked as AI Goal (optional cycleId). */
export const getAiGoals = async (cycleId = null) => {
  const params = {};
  if (cycleId) params.cycleId = cycleId;
  const response = await apiInstance.get(`${BASE_URL}/goals/ai`, { params });
  return response.data;
};

export const createGoal = async (goalData) => {
  const response = await apiInstance.post(`${BASE_URL}/goals`, goalData);
  return response.data;
};

export const updateGoal = async (goalId, goalData) => {
  const response = await apiInstance.put(`${BASE_URL}/goals/${goalId}`, goalData);
  return response.data;
};

export const deleteGoal = async (goalId) => {
  const response = await apiInstance.delete(`${BASE_URL}/goals/${goalId}`);
  return response.data;
};

export const approveGoal = async (goalId) => {
  const response = await apiInstance.put(`${BASE_URL}/goals/${goalId}/approve`);
  return response.data;
};

export const rejectGoal = async (goalId, reason) => {
  const response = await apiInstance.put(`${BASE_URL}/goals/${goalId}/reject`, { reason });
  return response.data;
};

export const setAchievedPercent = async (goalId, achievedPercent) => {
  const response = await apiInstance.put(`${BASE_URL}/goals/${goalId}/achieved`, { achievedPercent });
  return response.data;
};

// Star / checklist flows: owner marks their goal completed (+ optional comment)
export const selfEvaluateGoal = async (goalId, completed, comment) => {
  const response = await apiInstance.put(`${BASE_URL}/goals/${goalId}/self-evaluate`, { completed, comment });
  return response.data;
};

// Star flow: manager confirms/rejects the completion claim
export const verifyGoal = async (goalId, verified) => {
  const response = await apiInstance.put(`${BASE_URL}/goals/${goalId}/verify`, { verified });
  return response.data;
};

export const calculateScore = async (cycleId, userId) => {
  const response = await apiInstance.post(`${BASE_URL}/goals/calculate-score`, { cycleId, userId });
  return response.data;
};

export const submitGoals = async (cycleId, userId = null) => {
  const response = await apiInstance.post(`${BASE_URL}/goals/submit`, { cycleId, userId });
  return response.data;
};

// ============================================================================
// PROJECTS DROPDOWN API (for Goals)
// ============================================================================

export const getProjectsDropdown = async () => {
  const response = await apiInstance.get('/api/v1/projects/dropdown');
  return response.data;
};

export const getGoalsByProject = async (projectId) => {
  const response = await apiInstance.get(`${BASE_URL}/goals/by-project/${projectId}`);
  return response.data;
};

export const getProjectsSummary = async (cycleId) => {
  const response = await apiInstance.get(`${BASE_URL}/goals/projects-summary`, { params: { cycleId } });
  return response.data;
};

// ============================================================================
// SOFT SKILLS APIs
// ============================================================================

export const getSoftSkills = async (cycleId, userId) => {
  const response = await apiInstance.get(`${BASE_URL}/soft-skills/${cycleId}/${userId}`);
  return response.data;
};

export const updateSoftSkills = async (cycleId, userId, ratings) => {
  const response = await apiInstance.put(`${BASE_URL}/soft-skills/${cycleId}/${userId}`, { ratings });
  return response.data;
};

// ============================================================================
// REVIEW SUMMARY APIs
// ============================================================================

export const getReviewSummary = async (cycleId, userId) => {
  const response = await apiInstance.get(`${BASE_URL}/review-summary/${cycleId}/${userId}`);
  return response.data;
};

export const updateReviewSummary = async (cycleId, userId, questions) => {
  const response = await apiInstance.put(`${BASE_URL}/review-summary/${cycleId}/${userId}`, { questions });
  return response.data;
};

export const markReviewComplete = async (cycleId, userId) => {
  const response = await apiInstance.post(`${BASE_URL}/review-summary/${cycleId}/${userId}/complete`);
  return response.data;
};

// ============================================================================
// TEAM MEMBERS APIs (Manager View)
// ============================================================================

export const getTeamMembers = async (cycleId = null, page = 1, limit = 12, search = '', submissionStatus = 'all', scope = 'all') => {
  const params = { page, limit };
  if (cycleId) params.cycleId = cycleId;
  if (search) params.search = search;
  if (submissionStatus && submissionStatus !== 'all') params.submissionStatus = submissionStatus;
  if (scope === 'direct') params.scope = 'direct';
  const response = await apiInstance.get(`${BASE_URL}/team-members`, { params });
  return response.data;
};

export const getManagerTeamStats = async (cycleId = null, scope = 'direct') => {
  const params = { scope: scope === 'all' ? 'all' : 'direct' };
  if (cycleId) params.cycleId = cycleId;
  const response = await apiInstance.get(`${BASE_URL}/manager-team-stats`, { params });
  return response.data;
};

export const getMemberCycleData = async (userId, cycleId) => {
  const response = await apiInstance.get(`${BASE_URL}/team-members/${userId}/cycles/${cycleId}`);
  return response.data;
};

export const getMemberReportees = async (userId, cycleId = null) => {
  const params = {};
  if (cycleId) params.cycleId = cycleId;
  const response = await apiInstance.get(`${BASE_URL}/team-members/${userId}/reportees`, { params });
  return response.data;
};

// ============================================================================
// DASHBOARD APIs
// ============================================================================

export const getDashboardStats = async (userId = null) => {
  const params = userId ? { userId } : {};
  const response = await apiInstance.get(`${BASE_URL}/dashboard`, { params });
  return response.data;
};

export const getLeaderboard = async (limit = 5) => {
  const response = await apiInstance.get(`${BASE_URL}/leaderboard`, { params: { limit } });
  return response.data;
};

export const getTeamStats = async () => {
  const response = await apiInstance.get(`${BASE_URL}/team-stats`);
  return response.data;
};

// ============================================================================
// TRENDS & ACTIVITY APIs (additive — separate optimized endpoints)
// ============================================================================

export const getMonthlyScores = async ({ range = '6m', scope = 'self' } = {}) => {
  const response = await apiInstance.get(`${BASE_URL}/monthly-scores`, { params: { range, scope } });
  return response.data;
};

export const getActivityFeed = async ({ limit = 10, scope = 'self' } = {}) => {
  const response = await apiInstance.get(`${BASE_URL}/activity-feed`, { params: { limit, scope } });
  return response.data;
};
